import { Dialog, Drawer, Icon } from "@mui/material";
import React, { useEffect, useState } from "react";

import "../../shared/css/ui.css";
import { BulleV2 } from "../../shared/types/Bubble";
import { Character } from "../../shared/types/Character";
import { Dico } from "../../shared/types/Dico";
import { Episode } from "../../shared/types/Episode";
import "../css/Reader.css";
import BubbleImage from "./Bubble/BubbleImage";
import BubbleVisual from "./Bubble/BubbleVisual";
import FooterEndSerie from "./Bubble/FooterEndSerie";
import FooterNextEp from "./Bubble/FooterNextEp";
import ListCharacters from "./ListCharacters";
import ReaderSettings, { FONT_SIZE_MAX, FONT_SIZE_MIN } from "./ReaderSettings";
import ReaderTopbar from "./ReaderTopbar";

const SETTINGS_STORAGE_KEY = "nbk-reader-settings";

type ReaderParams = {
  isDys: boolean;
  fontSize: number;
};

type Props = {
  bubbles: BulleV2[] | undefined;
  episode: Episode | undefined;
  idSerie: number;
  characters: Character[];
  nextEp: Episode | undefined;
  returnTo: string;
  setViewableItem: (bubble: BulleV2, index: number) => void;
  params: ReaderParams;
  onExit: () => void;
};

// Reading preferences are remembered between episodes on this device
const readStoredSettings = (): Partial<ReaderParams> => {
  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const storeSettings = (settings: ReaderParams) => {
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable (private mode...): settings simply won't be remembered
  }
};

const useReadingProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return progress;
};

const ReaderVisual = (props: Props) => {
  const { bubbles, episode, idSerie, characters, nextEp, params, returnTo, setViewableItem, onExit } = props;

  const [dys, setDYS] = useState(() => readStoredSettings().isDys ?? params.isDys);
  const [fontSize, setFontSize] = useState(() => {
    const stored = readStoredSettings().fontSize ?? params.fontSize;
    return Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Number(stored) || FONT_SIZE_MIN));
  });

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [dico, setDico] = useState<Dico | null>(null);

  const progress = useReadingProgress();

  useEffect(() => {
    storeSettings({ isDys: dys, fontSize });
  }, [dys, fontSize]);

  const renderBubble = (b: BulleV2, index: number) => {
    let compToReturn;
    switch (b.type) {
      case "image":
        compToReturn = <BubbleImage key={b.uid} bubble={b} />;
        break;
      case "speaker":
        compToReturn = (
          <BubbleVisual
            key={b.uid}
            fontSize={fontSize}
            isDYS={dys}
            bubble={b}
            index={index}
            viewableItem={setViewableItem}
            onDico={setDico}
          />
        );
        break;
    }

    if (bubbles && index === bubbles.length - 1) {
      return (
        <React.Fragment key={b.uid}>
          {compToReturn}
          {nextEp !== undefined ? (
            <FooterNextEp
              params={{ isDys: dys, fontSize }}
              idSerie={idSerie}
              nextEp={nextEp}
              returnTo={returnTo}
              onExit={onExit}
            />
          ) : (
            <FooterEndSerie onExit={onExit} />
          )}
        </React.Fragment>
      );
    }

    return compToReturn;
  };

  const panelContent = (
    <div className="rd-panel">
      <ReaderSettings isDys={dys} fontSize={fontSize} onDysChange={setDYS} onFontSizeChange={setFontSize} />
      <ListCharacters characters={characters} />
    </div>
  );

  return (
    <div className="rd">
      <ReaderTopbar
        episode={episode}
        onExit={onExit}
        progress={progress}
        action={
          <button
            type="button"
            className="rd-icon-btn rd-topbar__settings"
            onClick={() => setIsPanelOpen(true)}
            aria-label="Options de lecture et personnages"
          >
            <Icon>tune</Icon>
          </button>
        }
      />

      <div className="rd-layout">
        <aside className="rd-sidebar" aria-label="Options de lecture">
          {panelContent}
        </aside>
        <main className="rd-page">{bubbles?.map((b, i) => renderBubble(b, i))}</main>
      </div>

      <Drawer
        anchor="right"
        open={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        PaperProps={{
          sx: { width: "min(360px, 88vw)", backgroundColor: "#120d35", backgroundImage: "none", color: "white" },
        }}
      >
        <div className="rd-panel__drawer">
          <div className="rd-panel__drawer-header">
            <h2>Options de lecture</h2>
            <button
              type="button"
              className="rd-icon-btn"
              onClick={() => setIsPanelOpen(false)}
              aria-label="Fermer les options de lecture"
            >
              <Icon>close</Icon>
            </button>
          </div>
          {panelContent}
        </div>
      </Drawer>

      <Dialog
        open={dico !== null}
        onClose={() => setDico(null)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="rd-dico-word"
        PaperProps={{
          sx: {
            backgroundColor: "#2B1A59",
            backgroundImage: "none",
            color: "white",
            borderRadius: "20px",
            margin: "16px",
            width: "calc(100% - 32px)",
          },
        }}
      >
        <div className="rd-dico">
          <span className="rd-dico__label">
            <Icon aria-hidden="true">menu_book</Icon>
            Dictionnaire
          </span>
          <h2 id="rd-dico-word" className="rd-dico__word">
            {dico?.word || dico?.WordWord}
          </h2>
          <p className="rd-dico__definition">{dico?.definition}</p>
          <button type="button" className="nbk-btn nbk-btn--primary" onClick={() => setDico(null)} autoFocus>
            J'ai compris
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default ReaderVisual;
